import React, { useMemo, useState } from "react";
import axios from "../../../utills/api";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Card,
  CardBody,
  Badge,
  Button,
  Row,
  Col,
  Spinner,
} from "reactstrap";

const RoomCard = ({
  room,
  tasks = [],
  refresh,
}) => {

  const [loading, setLoading] =
    useState(false);

  const task = useMemo(() => {

    const roomTasks = tasks.filter(
      (t) =>
        t.roomId === room._id ||
        t.roomId?._id === room._id
    );


    return roomTasks.sort(
      (a, b) =>
        new Date(b.createdAt) -
        new Date(a.createdAt)
    )[0];


  }, [tasks, room]);

  const openWhatsApp = (task) => {

    if (!task?.assignedToName || !task?.assignedMobile) {
      return;
    }
    const taskUrl =
      `${window.location.origin}/housekeeping-task/${task._id}`;
    const message = `🧹 HOUSEKEEPING TASK

━━━━━━━━━━━━━━━━━━━━

🏨 Hotel Cleaning Task

🚪 Room
${task.roomNumber}

⚡ Priority
${task.priority}

🧹 Task Type
${task.taskType}

━━━━━━━━━━━━━━━━━━━━

👉 Open Task

${taskUrl}

Please start the housekeeping task.

Thank You.
`;


    window.open(

      `https://wa.me/91${task.assignedMobile}?text=${encodeURIComponent(message)}`,

      "_blank"

    );

  };

  const getStatusColor = (status) => {
    switch (
    (status || "").toUpperCase()
    ) {
      case "CLEAN":
        return "success";

      case "DIRTY":
        return "danger";

      case "CLEANING":
        return "warning";

      case "INSPECTION":
        return "info";

      default:
        return "secondary";
    }
  };

  const getPriorityColor = (
    priority
  ) => {
    switch (
    (priority || "").toUpperCase()
    ) {
      case "VIP":
        return "danger";

      case "HIGH":
        return "warning";

      case "NORMAL":
        return "primary";

      default:
        return "secondary";
    }
  };

  const startCleaning = async () => {

    try {

      setLoading(true);


      const res = await axios.post(
        "/housekeeping-task",
        {
          roomId: room._id,

          priority:
            room.isVipRoom
              ? "VIP"
              : "NORMAL",

          taskType:
            "CLEANING",
        }
      );


      toast.success(
        `Assigned to ${res.data.assignedToName}`
      );


      openWhatsApp(
        res.data
      );


      refresh();


    } catch (err) {

      console.error(err);

      toast.error(
        err.response?.data?.message ||
        "Unable to assign cleaning task"
      );

    }
    finally {

      setLoading(false);

    }

  };

  const completeCleaning =
    async () => {
      try {
        if (!task?._id) return;

        setLoading(true);

        await axios.put(
          `/housekeeping-task/${task._id}/complete`
        );


        toast.success(
          "Room marked as clean"
        );


        refresh();
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

  const createMaintenance =
    async () => {
      try {
        setLoading(true);

        await axios.post(
          "/housekeeping-task",
          {
            roomId: room._id,

            priority:
              room.isVipRoom
                ? "VIP"
                : "HIGH",

            taskType:
              "MAINTENANCE",
          }
        );

        refresh();
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

  const borderColor =
    room.housekeepingStatus ===
      "CLEAN"
      ? "#198754"
      : room.housekeepingStatus ===
        "DIRTY"
        ? "#dc3545"
        : room.housekeepingStatus ===
          "INSPECTION"
          ? "#0dcaf0"
          : "#ffc107";

  const isAssigned =
    task &&
    (
      task.status === "ASSIGNED" ||
      task.status === "IN_PROGRESS"
    );

  return (

    <Card
      className="shadow-sm h-100"
      style={{
        borderBottom: `6px solid ${borderColor}`,
      }}
    >
      <CardBody>

        {loading && (
          <div className="text-center mb-2">
            <Spinner size="sm" />
          </div>
        )}

        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="d-flex align-items-center gap-2">
              <h5 className="mb-0">
                Room {room.roomNumber}
              </h5>
              {room.isVipRoom && (
                <Badge color="danger">
                  VIP
                </Badge>
              )}
            </div>
            <small className="text-muted">
              {room.roomType?.name}
            </small>

          </div>

          <Badge
            color={getStatusColor(
              room.housekeepingStatus
            )}
          >
            {
              room.housekeepingStatus
            }
          </Badge>
        </div>
        <hr />
        <Row>
          <Col xs={6}>
            <small className="text-muted">
              Floor
            </small>

            <div>
              {room.floor}
            </div>
          </Col>

          <Col xs={6}>
            <small className="text-muted">
              Building
            </small>
            <div>
              {room.building}
            </div>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col xs={6}>
            <small className="text-muted">
              Occupancy
            </small>

            <div>
              {
                room.occupancyStatus
              }
            </div>
          </Col>

          <Col xs={6}>
            <small className="text-muted">
              Operational
            </small>

            <div>
              {
                room.operationalStatus
              }
            </div>
          </Col>

        </Row>

        <Row className="mt-3">

          <Col xs={6}>
            <small className="text-muted">
              Wing
            </small>

            <div>
              {room.wing}
            </div>
          </Col>

          <Col xs={6}>
            <small className="text-muted">
              Smoking
            </small>

            <div>
              {room.smoking
                ? "Yes"
                : "No"}
            </div>
          </Col>

        </Row>

        <hr />

        <div className="mt-3">

          <div className="d-flex justify-content-between">

            <div>
              <small className="text-muted">
                Cleaning Status
              </small>
              <div>
                {room.housekeepingStatus === "DIRTY"
                  ? "PENDING CLEANING"
                  : room.housekeepingStatus === "CLEANING"
                    ? "IN PROGRESS"
                    : room.housekeepingStatus === "CLEAN"
                      ? "COMPLETED"
                      : task?.status}
              </div>
            </div>
          </div>

          {/* DIRTY ROOM */}
          {room.housekeepingStatus === "DIRTY" && !isAssigned && (

            <Button
              color="danger"
              size="sm"
              className="mt-3 w-100"
              onClick={startCleaning}
              disabled={loading}
            >

              {
                loading ?

                  (
                    <>
                      <Spinner size="sm" />
                      Assigning...
                    </>
                  )

                  :

                  (
                    "Assign Cleaning"
                  )
              }
            </Button>
          )}
          {
            isAssigned && (

              <Button
                color="warning"
                size="sm"
                className="mt-3 w-100"
                disabled
              >
                <i className="ri-user-line me-1"></i>
                Assigned :
                {
                  task.assignedToName
                  ||
                  task.assignedName
                  ||
                  "Housekeeper"
                }
              </Button>
            )
          }
          {/* CLEANING ROOM */}
          {
            room.housekeepingStatus === "CLEANING"
            &&
            task?.status !== "COMPLETED"
            &&
            (
              <Button
                color="success"
                size="sm"
                className="mt-3 w-100"
                onClick={completeCleaning}
                disabled={loading}
              >
                {
                  loading ?
                    (
                      <>
                        <Spinner size="sm" />
                        Completing...
                      </>
                    )
                    :
                    (
                      "Complete Cleaning"
                    )
                }
              </Button>
            )
          }
        </div>
      </CardBody>
    </Card>
  );
};

export default RoomCard;